import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styles } from './css';

import '@Root/lib/lit-3form-block/components/cta-text';
import { logErrorToSentry } from '../../../../helpers/helpers';
import { imagebossUrl } from '../../../../helpers/imageboss';
import { GA4ecommerceAnalyticsObject } from '../../../../helpers/analytics';
import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import {
  LIT_CSS_VARIABLES,
  GRID_LAYOUT,
  DIALOG_STYLES,
} from '@Root/lib/lit-styles.js';

import { fetchCart, addToCart } from '@Root/spree/api';
import {
  updateCartIndicator,
  changeProductImageInModal,
  changeProductNameInModal,
  changeProductSizeInfoInModal,
} from '@Root/spree/dom';

import { useCartConfirmationModal } from '../../../../helpers/helpers';

const getInit = { method: 'GET', headers: { Accept: 'application/json' } };
export class OrderSheetBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      _termsAccepted: { type: Boolean },
      _alertTermsRequired: { type: Boolean },
      _quantitySelected: { type: Number },
      _selectedGaugeId: { type: Number },
      _selectedEdgeFinishValue: { type: String },
      _gaugesData: { type: Array },
      _my3formLaminates: { type: Array },
      _currentSelectionPDMA: { type: Object },
      _currentSelectionSPREE: { type: Object },
      _fullSpreeData: { type: Object },
      _placingOrder: { type: Boolean },
      _currentPromptId: { type: String },
      _spreeImageUrl: { type: String },
      _showGaugesHelp: { type: Boolean },
      _showEdgeFinishHelp: { type: Boolean },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      DIALOG_STYLES,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));
    this._termsAccepted = false;
    this._alertTermsRequired = false;
    this._quantitySelected = 1;
    this._selectedGaugeId = 5; // this is the 3/8ths gauge
    this._selectedEdgeFinishValue = 'sawcut';
    this._gaugesData = [];
    this._my3formLaminates = [];
    this._currentSelectionPDMA = {};
    this._currentSelectionSPREE = {};
    this._fullSpreeData = {};
    this._placingOrder = false;
    this._currentPromptId = '';
    this._spreeImageUrl = '';
    this._showGaugesHelp = false;
    this._showEdgeFinishHelp = false;
  }

  async connectedCallback() {
    super.connectedCallback();
    this._my3formLaminates = this.content.my3form_laminates || {};
    this._finishesData = this.content.pdma_finishes;
    this.setupSpreeData();
    this.setupGaugeRadioData();
    this.setCurrentSelectionPDMA();
    this.setCurrentSelectionSPREE();

    document.addEventListener('panel-image-selected', e => {
      if (Array.isArray(e.detail.imageUrl)) {
        const baseUrl = e.detail?.imageUrl[1]?.src || null;
        if (baseUrl)
          this._spreeImageUrl = imagebossUrl(baseUrl, {
            width: 258,
          });
      }
    });
  }

  render() {
    return html`
      <div class="barrel-wrapper print-none" id="#order-sheet-barrel">
        <div class="inputs">
          <div class="input-row-wrapper">
            <span class="input-row-header barrel-heavy-text">
              <p class="barrel-heavy-text">Gauge</p>
              <b
                class="more-info barrel-heavy-text"
                @click="${() => (this._showGaugesHelp = !this._showGaugesHelp)}"
                >&#9432;</b
              >
            </span>
            <span class="input-row-value">
              <div class="radio-group">
                ${this._gaugesData.length
                  ? html`
                      <div class="radio-inputs">
                        ${this._gaugesData.map(gaugeOption => {
                          return html`
                            <span class="radio-wrapper">
                              <input
                                type="radio"
                                name="gauge"
                                id="gauge-${gaugeOption.id}"
                                value="${gaugeOption.id}"
                                ?checked=${gaugeOption.default}
                                @change=${this.updateSelectedGauge}
                              />
                              <label for="gauge-${gaugeOption.id}">
                                ${gaugeOption.display}
                                ${gaugeOption.id == 5
                                  ? this.standardGaugeText
                                  : ''}
                              </label>
                            </span>
                          `;
                        })}
                      </div>
                    `
                  : html``}
              </div>
            </span>
            ${this._showGaugesHelp
              ? this.helpPromptHTML('gauges-prompt', this.gaugesPromptData)
              : html``}
          </div>

          <div class="input-row-wrapper">
            <span class="input-row-header">
              <p class="barrel-heavy-text">Panel Size</p>
            </span>
            <span class="input-row-value">
              <p>48"x96"</p>
            </span>
          </div>

          ${this.hasStandardFrontFinish || this.hasStandardBackFinish
            ? html`
                <div class="input-row-wrapper">
                  <span class="input-row-header">
                    <p class="barrel-heavy-text">Surface Finish</p>
                  </span>
                  <span class="input-row-value">
                    <p>
                      ${this.surfaceFinishHTML}
                    </p>
                  </span>
                </div>
              `
            : html``}

          <div class="input-row-wrapper">
            <span class="input-row-header">
              <p class="barrel-heavy-text">Edge finish</p>
              <b
                class="more-info barrel-heavy-text"
                @click="${() =>
                  (this._showEdgeFinishHelp = !this._showEdgeFinishHelp)}"
                >&#9432;</b
              >
            </span>
            <span class="input-row-value">
              <div class="radio-group">
                ${this.edgeFinishingRadioData
                  ? html`
                      <div class="radio-inputs">
                        ${this.edgeFinishingRadioData.map(edgeFinishOption => {
                          return html`
                            <span class="radio-wrapper">
                              <input
                                type="radio"
                                name="finish"
                                id="finish-${edgeFinishOption.value}"
                                value="${edgeFinishOption.value}"
                                ?checked=${edgeFinishOption.default}
                                @change="${this.updateSelectedEdgeFinish}"
                              />
                              <label for="finish-${edgeFinishOption.value}"
                                >${edgeFinishOption.name}</label
                              >
                            </span>
                          `;
                        })}
                      </div>
                    `
                  : html``}
              </div>
            </span>
            ${this._showEdgeFinishHelp
              ? this.helpPromptHTML(
                  'edge-finishes-prompt',
                  this.edgeFinishesPromptData
                )
              : html``}
          </div>

          <div class="input-row-wrapper">
            <span class="input-row-header">
              <p class="barrel-heavy-text">Lead Time</p>
            </span>
            <span class="input-row-value">
              <p>4 weeks (not including transit time)</p>
            </span>
          </div>

          ${this.validPrice
            ? html`
                <div class="input-row-wrapper">
                  <span class="input-row-header">
                    <p class="barrel-heavy-text">Qty</p>
                  </span>
                  <span class="input-row-value">
                    <input
                      type="number"
                      .value="${this._quantitySelected}"
                      min="1"
                      step="1"
                      @keyup="${this.sheetQtyChanged}"
                    />
                  </span>
                </div>

                <div class="input-row-wrapper no-border">
                  <span class="input-row-header">
                    <p class="barrel-heavy-text total-price-header">
                      Total (USD)
                    </p>
                  </span>
                  <span class="input-row-value">
                    <p class="total-price-text">$${this.totalPrice}</p>
                    <p>${this.priceDisclaimerText}</p>
                  </span>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="terms"
                    id="terms"
                    @change="${this.termsClicked}"
                  />
                  <label
                    class=${classMap({
                      terms: true,
                      'terms-label': !this._alertTermsRequired,
                      'terms-label-alert': this._alertTermsRequired,
                    })}
                    for="terms"
                  >
                    I agree to the
                    <a href="/terms" target="_blank">Terms and Conditions</a>.
                    All sales are final.
                  </label>
                  <p class="barrel-text questions">
                    If you have any questions about your order please contact
                    your Materials Consultant or
                    <u @click="${this.openChat}">
                      chat with a Customer Service Rep.
                    </u>
                  </p>
                </div>
                <div class="ctas-wrapper">
                  <cta-button
                    cta-data=${this.primaryCtaData}
                    cta-type="primary"
                    @click="${this.addToSpreeCart}"
                    ?disabled="${this.orderBtnDisabled}"
                  ></cta-button>
                  <cta-button
                    .ctaData=${this.secondaryCtaData}
                    cta-type="secondary"
                  ></cta-button>
                </div>
                <div>
                  <p>
                    Have terms with us? Please contact us at
                    customer.support@3-form.com
                  </p>
                </div>
                <div class="chat-prompt">
                  <p class="barrel-text">
                    <u @click="${
                      this.openChat
                    }">Chat</a></u> with our Customer Service
                    team if you:
                  </p>
                  <ul>
                    <li>
                      Need a different surface finish, gauge, edge finish, diffusion
                      layer, etc. than what is listed.
                    </li>
                    <li>Plan to backlight the panel or adhere it directly to a wall.</li>
                    <li>
                      Are unsure which gauge is appropriate for your application. Note
                      that Varia panels have varied degrees of deflection.
                    </li>
                  </ul>
                  <p class="barrel-text">
                    Shipping is only available within the US and varies based on the
                    quantity of panels ordered and the ship to location.
                  </p>
                </div>
              `
            : html`
                <div class="contingecy-text-row">
                  <p class="barrel-text">
                    Not available for online ordering at this time.
                    <u>
                      <a href="/request-quote" target="_blank">
                        Request a Quote
                      </a>
                    </u>
                  </p>
                </div>
              `}
        </div>
      </div>
    `;
  }

  get descriptionText() {
    return this.content.text;
  }

  get orderBtnDisabled() {
    return !this._termsAccepted || this._placingOrder;
  }

  get totalPrice() {
    const price = this._currentSelectionSPREE?.price || null;
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    const priceNumber = price
      ? parseFloat(price.replace(/[$,]/g, '')) * this._quantitySelected
      : null;
    return priceNumber
      ? `${priceNumber.toLocaleString('en-US', options)} (USD)`
      : null;
  }

  get priceDisclaimerText() {
    return this._currentSelectionPDMA?.spree_product?.shipping_included
      ? 'Shipping included'
      : 'Taxes and shipping not included';
  }

  get validPrice() {
    return !!this.totalPrice;
  }

  setCurrentSelectionPDMA() {
    if (this._selectedGaugeId)
      this._currentSelectionPDMA =
        this._my3formLaminates.find(
          obj =>
            obj.pdma_gauge_id === this._selectedGaugeId &&
            obj.spree_product.edge_finish === this._selectedEdgeFinishValue
        ) || null;
  }

  async setCurrentSelectionSPREE() {
    if (!window.SpreeAPI || !this._currentSelectionPDMA) return;
    const spreeHost = window.SpreeAPI.host;
    const externalId = this._currentSelectionPDMA.spree_product.id;

    const url = `${spreeHost}/api/v2/storefront/products/${externalId}?include=product_properties`;
    const options = {
      method: 'GET',
      headers: { Accept: 'application/vnd.api+json' },
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      this._fullSpreeData = data;
      this._currentSelectionSPREE = data?.data?.attributes || null;
      if (this._fullSpreeData) {
        this.sendAnaylticsEvent('view_item');
      }
      this.requestUpdate();
    } catch (error) {
      this._fullSpreeData = {};
      logErrorToSentry(error);
    }
  }

  updateSelectedEdgeFinish(event) {
    if (event) {
      this._selectedEdgeFinishValue = event.target.value;
      this.updateSelected();
    }
  }

  updateSelectedGauge(event) {
    if (event && parseFloat(event.target.value)) {
      this._selectedGaugeId = parseFloat(event.target.value);
      this.updateSelected();
    }
  }

  updateSelected() {
    this.setCurrentSelectionPDMA();
    this.setCurrentSelectionSPREE();
  }

  termsClicked(event) {
    this._termsAccepted = event.target.checked;
    if (this._termsAccepted && this._alertTermsRequired) {
      this._alertTermsRequired = false;
    }
  }

  sheetQtyChanged(event) {
    if (event) {
      if (isNaN(event.target.value)) return;
      const newValue = event.target.value < 1 ? 1 : event.target.value;
      this._quantitySelected = Math.ceil(newValue);
      this.requestUpdate();
    }
  }

  get primaryCtaData() {
    return JSON.stringify({
      text: 'Add to Cart',
      fullWidth: true,
    });
  }

  async setupSpreeData() {
    this.cart = await fetchCart();
    if (this.cart) updateCartIndicator(this.cart.data.attributes.item_count);
  }

  async addToSpreeCart() {
    if (!this._termsAccepted || this.orderBtnDisabled) {
      this._alertTermsRequired = true;
      this.requestUpdate();
      return;
    }

    useCartConfirmationModal();
    this._placingOrder = true;

    this.cart = await addToCart(
      this._currentSelectionPDMA.spree_product.variant_id,
      this._quantitySelected,
      'full-panel'
    );
    let header;
    const subheader = this.productSubheader,
      disusedHeaderText = ` ${this.productSubheader}`;

    if (
      disusedHeaderText !== ' ' &&
      this.productHeader.includes(disusedHeaderText)
    ) {
      header = this.productHeader.replace(disusedHeaderText, '');
    } else {
      header = this.productHeader;
    }
    if (this.cart) {
      this.closeDialogModal();
      this.sendAnaylticsEvent('add_to_cart');
      updateCartIndicator(this.cart.item_count);
      changeProductImageInModal(this._spreeImageUrl);
      changeProductNameInModal(header, subheader);
      changeProductSizeInfoInModal(
        this.selectedPanelSizeText,
        this.selectedPanelGaugeText,
        true
      );

      // TODO: STUFF FOR NEW DIALOG
      // const confirmDialog = document.querySelector('cart-confirm-dialog');
      // if (confirmDialog) {
      //   const dialog = confirmDialog.shadowRoot.querySelector('dialog');
      //   if (dialog) dialog.showModal();
      // }
    }

    this._placingOrder = false;
  }

  get secondaryCtaData() {
    const externalId = this._currentSelectionPDMA?.spree_product?.id || '';
    const ctaURL = `/estimates/${externalId}?qty=${this._quantitySelected}`;

    return {
      text: 'Print Estimate',
      url: ctaURL,
      fullWidth: true,
      target: '_blank',
    };
  }

  get barrelTitle() {
    const titleStr = this.content.headerStr.toUpperCase() || '';
    const panelStr = '48"X 96" PANEL';
    return `${titleStr} ${panelStr}`.trim();
  }

  setupGaugeRadioData() {
    let foundDefault = false;
    const gauges = this.content.pdma_gauges
      .filter(gauge =>
        this._my3formLaminates.some(
          laminate => laminate.pdma_gauge_id == gauge.id
        )
      )
      .map(gauge => {
        if (gauge.id === this._selectedGaugeId) {
          gauge.default = true;
          foundDefault = true;
          this._selectedGaugeId = gauge.id;
        }
        return gauge;
      });
    if (!foundDefault && gauges.length) {
      this._selectedGaugeId = gauges[0].id;
      gauges[0].default = true;
    }
    this._gaugesData = gauges;
  }

  get edgeFinishingRadioData() {
    return [
      {
        value: 'sawcut',
        name: 'Sawcut (unfinished)',
        default: true,
      },
      {
        value: 'polished',
        name: 'Flame Polished',
        default: false,
      },
    ];
  }

  dialogClick(event) {
    if (event.target.getAttribute('id') === this._currentPromptId) {
      this.closeCurrentHelpPrompt();
    }
  }

  closeCurrentHelpPrompt() {
    switch (this._currentPromptId) {
      case 'gauges-prompt':
        this._showGaugesHelp = false;
        break;
      case 'edge-finishes-prompt':
        this._showEdgeFinishHelp = false;
        break;
    }
  }

  helpPromptHTML(dialogId, data) {
    return html`
      <div id=${dialogId} @click="${this.dialogClick}" class="full-row">
        <div class="prompt-content">
          <div class="prompt-header">
            <p>${data.header}</p>
          </div>
          <div class="prompt-body">
            <p>
              ${data.description}
            </p>
            <div class="prompt-images">
              ${data.images.map(image => {
                return html`
                  <span class="prompt-image-wrapper">
                    <img src="${this.dialogImageUrl(dialogId, image)}" />
                    <p class="barrel-heavy-text">
                      ${image.title}
                    </p>
                    ${image.description
                      ? html`
                          <p class="description">${image.description}</p>
                        `
                      : html``}
                  </span>
                `;
              })}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  openChat() {
    if (typeof brandembassy === 'function') {
      this.closeDialogModal();
      brandembassy('openChatWindow');
    }
  }

  dialogImageUrl(dialogId, image) {
    const dimensions = {
      width: 400,
      height: 400,
    };

    if (dialogId === 'gauges-prompt') {
      dimensions.height = 200;
    }

    return imagebossUrl(image.url, dimensions);
  }

  sendAnaylticsEvent(eventType = 'view_item') {
    if (typeof gtag !== 'function') return;
    if (Object.keys(this._fullSpreeData).length > 0) {
      const itemObject = GA4ecommerceAnalyticsObject(this._fullSpreeData);
      if (itemObject) {
        const value = Number(itemObject.price) || 0;
        const fullAnalyticsObj = {
          currency: 'USD',
          value: value,
          items: [itemObject],
        };
        gtag('event', eventType, fullAnalyticsObj);
      }
    }
  }

  get edgeFinishesPromptData() {
    return {
      header: 'What edge finish should you select?',
      description:
        'The edge finish you choose often depends on how it will be installed. If the edges will be exposed you will want a polished edge that will give it a nice finished aesthetic. If the edges will be enclosed/fully framed in hardware or millwork, then a saw cut edge will suffice.',
      images: [
        {
          url:
            'https://img.imageboss.me/mkt-images-prod/:dimensions/4cmzvo79r36tgwwqehbeq5a1qokk',
          title: 'Sawcut',
        },
        {
          url:
            'https://img.imageboss.me/mkt-images-prod/:dimensions/ptqfa16c3bywp1fa9m5vtb652sjf',
          title: 'Flame Polished',
        },
      ],
    };
  }

  get gaugesPromptData() {
    return {
      header: 'What panel gauge is right for you?',
      description:
        'Varia comes in various gauges including ¼”, ⅜” and ½”. Choosing the right gauge is dependent on the use of the material. Please see below for general guidance on how to choose the right gauge for your project. Note: thinner gauges of material will deflect more than thicker gauges.',
      images: [
        {
          url:
            'https://img.imageboss.me/mkt-images-prod/:dimensions/61pphvghqt2npzi03tlxp3q9f8xb',
          title: '1/4"',
          description:
            'This gauge is most often used with our Suspend Hardware. The thinner gauge means the panel will deflect (or move) more when there is impact to the panel.',
        },
        {
          url:
            'https://img.imageboss.me/mkt-images-prod/:dimensions/ptqfa16c3bywp1fa9m5vtb652sjf',
          title: '3/8"',
          description:
            'This is our most common and versatile gauge and is used in many of our standard hardware solutions including Iltuo and Sliding Door systems. This gauge will have less deflection than a ¼” panel.',
        },
        {
          url:
            'https://img.imageboss.me/mkt-images-prod/:dimensions/59iytrzb6d1xc5ay5sjkoav56flh',
          title: '1/2"',
          description:
            'This is one of our most rigid gauges and has the least amount of deflection of the three. This is recommended in heavy traffic applications and/or where the hardware is minimal like Top Support.',
        },
      ],
    };
  }

  get productHeader() {
    return this._currentSelectionPDMA.header;
  }

  get productSubheader() {
    return this._currentSelectionPDMA.subheader;
  }

  get selectedPanelSizeText() {
    return '48" x 96" PANEL';
  }

  get selectedPanelGaugeText() {
    return `${this.selectedGauge.display} GAUGE`;
  }

  get selectedGauge() {
    return this._gaugesData.find(({ id }) => id === this._selectedGaugeId);
  }

  get standardGaugeText() {
    return '(standard)';
  }

  get surfaceFinishHTML() {
    return html`
      ${this.hasMatchingSurfaceFinishes
        ? this.matchingSurfaceFinishHTML
        : this.individualSurfaceFinishHTML}
    `;
  }

  get matchingSurfaceFinishHTML() {
    // The HTML is gross but it's necessary to prevent undue whitespace from
    // being underlined.
    return html`
      <u
        ><a href="/finishes" target="_blank"
          >${this.selectedFrontFinish.display}</a
        ></u
      >
      finish (both sides)
    `;
  }

  get individualSurfaceFinishHTML() {
    // There is an earlier check for one of these existing so this should always
    // render something.
    if (this.hasStandardFrontFinish && this.hasStandardBackFinish) {
      return html`
        ${this.frontFinishHTML} and ${this.backFinishHTML}
      `;
    } else if (this.hasStandardFrontFinish) {
      return this.frontFinishHTML;
    } else {
      return this.backFinishHTML;
    }
  }

  get frontFinishHTML() {
    if (this.hasStandardFrontFinish)
      // The HTML is gross but it's necessary to prevent undue whitespace from
      // being underlined.
      return html`
        <u
          ><a href="/finishes" target="_blank"
            >${this.selectedFrontFinish.display}</a
          ></u
        >
        finish (front)
      `;
  }

  get backFinishHTML() {
    if (this.hasStandardBackFinish)
      // The HTML is gross but it's necessary to prevent undue whitespace from
      // being underlined.
      return html`
        <u
          ><a href="/finishes" target="_blank"
            >${this.selectedBackFinish.display}</a
          ></u
        >
        finish (back)
      `;
  }

  get selectedFrontFinish() {
    return this._finishesData.find(
      ({ id }) => id == this._currentSelectionPDMA.pdma_front_finish_id
    );
  }

  get selectedBackFinish() {
    return this._finishesData.find(
      ({ id }) => id == this._currentSelectionPDMA.pdma_back_finish_id
    );
  }

  get hasMatchingSurfaceFinishes() {
    return (
      this._currentSelectionPDMA.pdma_front_finish_id ==
      this._currentSelectionPDMA.pdma_back_finish_id
    );
  }

  get hasStandardFrontFinish() {
    return this.selectedFrontFinish?.id;
  }

  get hasStandardBackFinish() {
    return this.selectedBackFinish?.id;
  }

  closeDialogModal() {
    document.dispatchEvent(
      new CustomEvent('closeDialogModal', {
        bubbles: true,
        composed: true,
      })
    );
  }
}
customElements.define('order-sheet-barrel', OrderSheetBarrel);
